import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaEdit, FaEye, FaPlus, FaSearch, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import Paginations from "../Customs/pagination/Paginations";
import { AccessItem } from "../Models/Models";
import { RootState } from "../Services/Store";
import WebService from "../Services/WebService";
import { cancelExpense, fetchExpenseMeta, fetchExpenses } from "../Services/expenses";
import AddExpensePanel from "./AddExpensePanel";
import styles from "./Expenses.module.css";

interface NamedOption {
  value: string;
  label: string;
}

interface AccountOption {
  account_id: number;
  account_name: string;
  account_type: string;
}

interface CreatorOption {
  user_id: number;
  username: string;
}

interface ExpenseRow {
  expense_id: number;
  expense_code?: string;
  expense_date?: string;
  category?: string;
  category_label?: string;
  description?: string;
  amount?: number | string;
  payment_method?: string;
  payment_method_label?: string;
  paid_from?: string;
  paid_from_label?: string;
  cash_bank_account_id?: number;
  cash_bank_account_name?: string;
  vendor_id?: number;
  vendor_payee?: string;
  reference_number?: string;
  created_by?: number;
  created_by_name?: string;
  status?: string;
  status_label?: string;
  notes?: string;
}

interface Summary {
  total_expenses?: number | string;
  cash_expenses?: number | string;
  bank_expenses?: number | string;
  other_expenses?: number | string;
  selected_period_expenses?: number | string;
  category_wise?: { category: string; category_label: string; amount: number | string }[];
  report?: { period_key: string; period_label: string; amount: number | string; count: number }[];
}

const ITEMS_PER_PAGE = 15;

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "₹0.00";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "₹0.00";
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value?: string): string {
  if (!value) return "—";
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-IN");
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN");
}

function ymd(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

function quarterStart(date: Date): Date {
  const q = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), q, 1);
}

const Expenses: React.FC = () => {
  const navigate = useNavigate();
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const expenseAccess =
    (accessList.find((a: AccessItem) => a.code === "expense_management") as AccessItem) ||
    ({} as AccessItem);
  const canWrite = !!expenseAccess.write;

  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [category, setCategory] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paidFrom, setPaidFrom] = useState("");
  const [accountId, setAccountId] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [status, setStatus] = useState("");
  const [period, setPeriod] = useState("MONTHLY");
  const [preset, setPreset] = useState("all");
  const [showPanel, setShowPanel] = useState(false);
  const [editRow, setEditRow] = useState<ExpenseRow | null>(null);

  const [categories, setCategories] = useState<NamedOption[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<NamedOption[]>([]);
  const [paidFromOptions, setPaidFromOptions] = useState<NamedOption[]>([]);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [creators, setCreators] = useState<CreatorOption[]>([]);
  const [reportPeriods, setReportPeriods] = useState<NamedOption[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredAccounts = useMemo(
    () => accounts.filter((a) => !paidFrom || a.account_type === paidFrom),
    [accounts, paidFrom]
  );

  useEffect(() => {
    fetchExpenseMeta()
      .then((res: any) => {
        const data = res?.data || {};
        setCategories(data.categories || []);
        setPaymentMethods(data.payment_methods || []);
        setPaidFromOptions(data.paid_from_options || []);
        setAccounts(data.accounts || []);
        setCreators(data.created_by_users || []);
        setReportPeriods(data.report_periods || []);
      })
      .catch(() => undefined);
    WebService.getAPI({ action: "/v1/door-management/vendors?limit=100&page_number=1" })
      .then((res: any) => setVendors(res?.data?.vendors || []))
      .catch(() => setVendors([]));
  }, []);

  const fetchList = useCallback(() => {
    setLoading(true);
    fetchExpenses({
      page,
      limit: ITEMS_PER_PAGE,
      fromDate,
      toDate,
      category,
      paymentMethod,
      paidFrom,
      cashBankAccountId: accountId,
      createdBy,
      status,
      search,
      period,
    })
      .then((res: any) => {
        setRows(res?.data?.expenses || []);
        setSummary(res?.data?.summary || null);
        setTotalPages(res?.data?.pagination?.total_pages || 1);
      })
      .catch(() => {
        setRows([]);
        setSummary(null);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  }, [
    page,
    fromDate,
    toDate,
    category,
    paymentMethod,
    paidFrom,
    accountId,
    createdBy,
    status,
    search,
    period,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const applyPreset = (id: string) => {
    setPreset(id);
    setPage(1);
    const now = new Date();
    if (id === "all") {
      setFromDate("");
      setToDate("");
      return;
    }
    if (id === "today") {
      const d = ymd(now);
      setFromDate(d);
      setToDate(d);
      setPeriod("DAILY");
      return;
    }
    if (id === "month") {
      setFromDate(ymd(new Date(now.getFullYear(), now.getMonth(), 1)));
      setToDate(ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
      setPeriod("MONTHLY");
      return;
    }
    if (id === "quarter") {
      const start = quarterStart(now);
      setFromDate(ymd(start));
      setToDate(ymd(new Date(start.getFullYear(), start.getMonth() + 3, 0)));
      setPeriod("QUARTERLY");
      return;
    }
    if (id === "half") {
      const firstHalf = now.getMonth() < 6;
      setFromDate(ymd(new Date(now.getFullYear(), firstHalf ? 0 : 6, 1)));
      setToDate(ymd(new Date(now.getFullYear(), firstHalf ? 6 : 12, 0)));
      setPeriod("HALF_YEARLY");
      return;
    }
    if (id === "year") {
      setFromDate(ymd(new Date(now.getFullYear(), 0, 1)));
      setToDate(ymd(new Date(now.getFullYear(), 11, 31)));
      setPeriod("YEARLY");
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 350);
  };

  const handleCancel = (row: ExpenseRow) => {
    Swal.fire({
      title: "Cancel this expense?",
      text: "The original amount stays on record. A reversal will be posted to the cash/bank account.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c0392b",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, cancel it",
    }).then((result) => {
      if (!result.isConfirmed) return;
      cancelExpense(row.expense_id)
        .then(() => {
          Swal.fire({ icon: "success", title: "Expense cancelled", timer: 1400, showConfirmButton: false });
          fetchList();
        })
        .catch(() => {
          Swal.fire({ icon: "error", title: "Could not cancel expense" });
        });
    });
  };

  const presets = [
    { id: "all", label: "All dates" },
    { id: "today", label: "Today" },
    { id: "month", label: "This month" },
    { id: "quarter", label: "This quarter" },
    { id: "half", label: "This half-year" },
    { id: "year", label: "This year" },
  ];

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Expenses</h1>
          <p className={styles.subtitle}>Record, track, and report business expenses</p>
        </div>
        {canWrite && (
          <button
            type="button"
            className={styles.addButton}
            onClick={() => {
              setEditRow(null);
              setShowPanel(true);
            }}
          >
            <FaPlus /> Add Expense
          </button>
        )}
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Total Expenses</div>
          <div className={styles.summaryValue}>{formatMoney(summary?.total_expenses)}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Cash Expenses</div>
          <div className={styles.summaryValue}>{formatMoney(summary?.cash_expenses)}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Bank Expenses</div>
          <div className={styles.summaryValue}>{formatMoney(summary?.bank_expenses)}</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Selected Period</div>
          <div className={styles.summaryValue}>{formatMoney(summary?.selected_period_expenses)}</div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchInput}>
          <FaSearch className={styles.searchIcon} />
          <input placeholder="Search ID, payee, description..." value={searchInput} onChange={handleSearch} />
        </div>
        <input
          type="date"
          className={styles.filterInput}
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPreset("custom");
            setPage(1);
          }}
        />
        <input
          type="date"
          className={styles.filterInput}
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPreset("custom");
            setPage(1);
          }}
        />
        <select
          className={styles.filterInput}
          value={preset}
          onChange={(e) => applyPreset(e.target.value)}
        >
          {preset === "custom" && <option value="custom">Custom dates</option>}
          {presets.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
        <select
          className={styles.filterInput}
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        >
          {(reportPeriods.length ? reportPeriods : [
            { value: "DAILY", label: "Daily" },
            { value: "MONTHLY", label: "Monthly" },
            { value: "QUARTERLY", label: "Quarterly" },
            { value: "HALF_YEARLY", label: "Half-Yearly" },
            { value: "YEARLY", label: "Yearly" },
          ]).map((p) => (
            <option key={p.value} value={p.value}>
              Report: {p.label}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.filterBar}>
        <select
          className={styles.filterInput}
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          className={styles.filterInput}
          value={paymentMethod}
          onChange={(e) => {
            setPaymentMethod(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All payment methods</option>
          {paymentMethods.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          className={styles.filterInput}
          value={paidFrom}
          onChange={(e) => {
            setPaidFrom(e.target.value);
            setAccountId("");
            setPage(1);
          }}
        >
          <option value="">All paid from</option>
          {paidFromOptions.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        <select
          className={styles.filterInput}
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All accounts</option>
          {filteredAccounts.map((a) => (
            <option key={a.account_id} value={a.account_id}>
              {a.account_name}
            </option>
          ))}
        </select>
        <select
          className={styles.filterInput}
          value={createdBy}
          onChange={(e) => {
            setCreatedBy(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All created by</option>
          {creators.map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {u.username}
            </option>
          ))}
        </select>
        <select
          className={styles.filterInput}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="POSTED">Posted</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </div>

      <div className={styles.columns}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>Report ({period.replaceAll("_", " ").toLowerCase()})</div>
          <div className={styles.tableWrap}>
            <table className={styles.table} style={{ minWidth: 480 }}>
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Count</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.report || []).length ? (
                  summary?.report?.map((bucket) => (
                    <tr key={bucket.period_key}>
                      <td>{bucket.period_label}</td>
                      <td>{bucket.count}</td>
                      <td>{formatMoney(bucket.amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className={styles.muted}>
                      No posted expenses in this period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>Category-wise Expenses</div>
          {(summary?.category_wise || []).length ? (
            <ul className={styles.breakdownList}>
              {summary?.category_wise?.map((item) => (
                <li key={item.category}>
                  <span>{item.category_label}</span>
                  <strong>{formatMoney(item.amount)}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.muted}>No category totals yet</div>
          )}
        </div>
      </div>

      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loader}>Loading expenses...</div>
        ) : rows.length ? (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Expense ID</th>
                  <th>Expense Date</th>
                  <th>Expense Category</th>
                  <th>Expense Description</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>Paid From</th>
                  <th>Vendor/Payee</th>
                  <th>Reference Number</th>
                  <th>Created By</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.expense_id}>
                    <td>{row.expense_code || `EXP-${row.expense_id}`}</td>
                    <td>{formatDate(row.expense_date)}</td>
                    <td>{row.category_label || row.category}</td>
                    <td>{row.description}</td>
                    <td>{formatMoney(row.amount)}</td>
                    <td>{row.payment_method_label || row.payment_method}</td>
                    <td>{row.paid_from_label || row.paid_from}</td>
                    <td>{row.vendor_payee || "—"}</td>
                    <td>{row.reference_number || "—"}</td>
                    <td>{row.created_by_name || row.created_by || "—"}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[row.status || "POSTED"]}`}>
                        {row.status_label || row.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          type="button"
                          className={`${styles.actionBtn} ${styles.viewBtn}`}
                          onClick={() => navigate(`/expenses/${row.expense_id}`)}
                        >
                          <FaEye /> View
                        </button>
                        {canWrite && row.status === "POSTED" && (
                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => {
                              setEditRow(row);
                              setShowPanel(true);
                            }}
                          >
                            <FaEdit /> Edit
                          </button>
                        )}
                        {canWrite && row.status === "POSTED" && (
                          <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.cancelAction}`}
                            onClick={() => handleCancel(row)}
                          >
                            <FaTimes /> Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.empty}>No expenses found.</div>
        )}
      </div>

      <Paginations
        totalItems={totalPages * ITEMS_PER_PAGE}
        itemsCountPerPage={ITEMS_PER_PAGE}
        changePage={setPage}
        changeCurrentPage={page}
      />

      {showPanel && (
        <AddExpensePanel
          open={showPanel}
          mode={editRow ? "edit" : "add"}
          editData={editRow}
          categories={categories}
          paymentMethods={paymentMethods}
          paidFromOptions={paidFromOptions}
          accounts={accounts}
          vendors={vendors}
          onClose={() => {
            setShowPanel(false);
            setEditRow(null);
          }}
          onSaved={fetchList}
        />
      )}
    </div>
  );
};

export default Expenses;
